/**
 * T265 — Claim Player landing page.
 *
 * Route: /claim/:token
 *
 * Lands a player on a confirmation page. Three states:
 *
 *   logged out → "Sign up or log in to claim this profile" + persona-
 *                hint param so signup defaults to player.
 *   logged in + can claim → "Claim now" button → POST → redirects to
 *                /account/player-profile/.
 *   already claimed by someone else → "This profile has already been
 *                claimed" message.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertCircle, CheckCircle2, ChevronRight, UserCheck,
} from "lucide-react";

import { claimPlayer } from "../../api/coredata.api";
import useAuthStore from "../../store/auth.store";


export default function ClaimPlayer() {
  const { token } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);  // null | true | false
  const [error, setError] = useState("");

  async function attemptClaim() {
    setBusy(true);
    setError("");
    try {
      const { data } = await claimPlayer(token);
      setDone(true);
      // Brief pause so the success state shows, then redirect.
      setTimeout(() => navigate("/account/player-profile"), 1500);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
        "Couldn't claim this profile.",
      );
      setDone(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-bg-main text-text-primary">
      <div className="max-w-md w-full bg-bg-primary border border-bg-secondary rounded-xl p-6 shadow-2xl">
        <div className="flex items-center gap-2 mb-4">
          <UserCheck className="w-6 h-6 text-button-primary" />
          <h1 className="text-xl font-bold text-text-primary">Claim your player profile</h1>
        </div>

        <p className="text-sm text-text-secondary mb-4">
          A scorer at one of your matches added you to ZenCricketData.
          Claim this profile to own your stats, build a public page,
          and be discoverable by scouts.
        </p>

        {done === true && (
          <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 mb-3 text-sm text-emerald-200">
            <CheckCircle2 className="inline w-4 h-4 text-emerald-300 mr-1" />
            Profile claimed. Redirecting you to your editor…
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 mb-3 text-sm text-rose-200 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 text-rose-300 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {!user ? (
          <>
            <p className="text-sm text-text-secondary mb-3">
              You'll need a free ZenCricketData account first.
            </p>
            <div className="flex gap-2">
              <Link
                to={`/signup?persona=player&next=/claim/${token}`}
                className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 bg-button-primary text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Sign up free
                <ChevronRight className="w-4 h-4" />
              </Link>
              <Link
                to={`/login?next=/claim/${token}`}
                className="flex-1 inline-flex items-center justify-center px-3 py-2 rounded-lg border border-bg-secondary bg-bg-secondary text-text-primary text-sm hover:bg-bg-main transition-colors"
              >
                Log in
              </Link>
            </div>
            <p className="mt-4 text-xs text-text-secondary text-center">
              The free tier gives you a public profile.{" "}
              Player Pro (₹199/mo) adds a verified badge + scout DMs.
            </p>
          </>
        ) : (
          <>
            <p className="text-sm text-text-secondary mb-3">
              You're signed in as <strong className="text-text-primary">{user.username}</strong>.
            </p>
            <button
              onClick={attemptClaim}
              disabled={busy || done === true}
              className="w-full inline-flex items-center justify-center gap-1 px-3 py-2 bg-button-primary text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {busy ? "Claiming…" : "Claim this profile"}
              {!busy && <ChevronRight className="w-4 h-4" />}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
